import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const Invoices = () => {
  const [appointments, setAppointments] = useState([]);
  const [paidStatus, setPaidStatus] = useState({});
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Get customer_id from logged in user
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const customer_id = user.id;

  useEffect(() => {
    if (!customer_id) return;
  fetch(`${process.env.REACT_APP_VENDOR_DOMAIN}/customer_appointments?user_id=${customer_id}`)
      .then(res => res.json())
      .then(data => {
        setAppointments(data.appointments || []);
        setLoading(false);
      });
  }, [customer_id]);

  // Lookup paid status for each appointment
  useEffect(() => {
    const fetchPaidStatus = async () => {
      const statusMap = {};
      for (const app of appointments) {
        if (app.status === 'completed' || app.status === 'accepted') {
          const params = new URLSearchParams({
            customer_id: app.customer_id || customer_id,
            vendor_id: app.vendor_id,
            service_detail_id: app.product_detail_id,
            start_time: app.start_time,
            end_time: app.end_time,
            appointment_id: app.id,
          });
          try {
            const res = await fetch(`${process.env.REACT_APP_VENDOR_DOMAIN}/invoices?${params.toString()}`);
            const data = await res.json();
            statusMap[app.id] = data.exists === 'yes' ? 'Paid' : 'Unpaid';
          } catch {
            statusMap[app.id] = 'Unpaid';
          }
        }
      }
      setPaidStatus(statusMap);
    };
    if (appointments.length > 0) fetchPaidStatus();
  }, [appointments, customer_id]);

  // Helper to calculate hours between start_time and end_time (supports AM/PM)
  const parseTime = (timeStr) => {
    if (!timeStr) return null;
    const match = timeStr.match(/^(\d{1,2}):(\d{2})\s*([AP]M)?$/i);
    if (!match) return null;
    let hour = parseInt(match[1], 10);
    const minute = parseInt(match[2], 10);
    const ampm = match[3] ? match[3].toUpperCase() : null;
    if (ampm === 'PM' && hour < 12) hour += 12;
    if (ampm === 'AM' && hour === 12) hour = 0;
    return hour * 60 + minute;
  };

  const getHours = (start, end) => {
    const startMinutes = parseTime(start);
    const endMinutes = parseTime(end);
    if (startMinutes === null || endMinutes === null) return 0;
    let diff = endMinutes - startMinutes;
    return diff > 0 ? (diff / 60) : 0;
  };

  if (loading) return (
    <div style={styles.container}>
      <div style={styles.loader}>Loading invoices...</div>
    </div>
  );

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>Invoices</h2>
      {appointments.length === 0 ? (
        <div style={styles.empty}>No appointments found.</div>
      ) : (
        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Date</th>
                <th style={styles.th}>Start Time</th>
                <th style={styles.th}>End Time</th>
                <th style={styles.thHourlyRate}>Rate</th>
                <th style={styles.th}>Hours</th>
                <th style={styles.th}>Total</th>
                <th style={styles.th}>Work Status</th>
                <th style={styles.th}>Pay Status</th>
                <th style={styles.th}></th>
              </tr>
            </thead>
            <tbody>
              {appointments
                .filter(app => app.status === 'completed' || app.status === 'accepted')
                .sort((a, b) => {
                  // Sort descending by date
                  const dateA = new Date(a.date);
                  const dateB = new Date(b.date);
                  return dateB - dateA;
                })
                .map(app => {
                  const hours = getHours(app.start_time, app.end_time);
                  const rate = app.hourly_rate || 15;
                  const total = (hours * rate).toFixed(2);
                  const bookingFee = (Number(total) * 0.10).toFixed(2);
                  const grandTotal = (Number(total) + Number(bookingFee)).toFixed(2);
                  const formattedDate = app.date
                    ? (() => {
                        const d = new Date(app.date);
                        const mm = String(d.getMonth() + 1).padStart(2, '0');
                        const dd = String(d.getDate()).padStart(2, '0');
                        const yyyy = d.getFullYear();
                        return `${mm}/${dd}/${yyyy}`;
                      })()
                    : '';
                  const payStatus = paidStatus[app.id] || 'Unpaid';
                  return (
                    <tr key={app.id} style={styles.tr}>
                      <td style={styles.td}>{formattedDate}</td>
                      <td style={styles.td}>{app.start_time}</td>
                      <td style={styles.td}>{app.end_time}</td>
                      <td style={styles.td}>${rate}</td>
                      <td style={styles.td}>{hours}</td>
                      <td style={styles.td}>
                        <div style={styles.totalBox}>
                          <span style={styles.serviceFee}>Service: <b>${total}</b></span>
                          <span style={styles.bookingFee}>Booking Fee (10%): <b>${bookingFee}</b></span>
                          <span style={styles.grandTotal}>Grand Total: <b>${grandTotal}</b></span>
                        </div>
                      </td>
                      <td style={{
                        ...styles.td,
                        color:
                          app.status === 'pending'
                            ? '#ff9800'
                            : app.status === 'accepted'
                            ? '#388e3c'
                            : app.status === 'rejected'
                            ? '#d32f2f'
                            : app.status === 'completed'
                            ? '#388e3c'
                            : '#333',
                        fontWeight: 'bold',
                        textTransform: 'capitalize',
                        letterSpacing: '0.5px',
                      }}>
                        {app.status}
                      </td>
                      <td style={{
                        ...styles.td,
                        color: payStatus === 'Paid' ? '#388e3c' : '#d32f2f',
                        fontWeight: 'bold',
                        textTransform: 'capitalize',
                        letterSpacing: '0.5px',
                      }}>
                        {payStatus}
                      </td>
                      <td style={styles.td}>
                        {(app.status === 'completed' || app.status === 'accepted') && payStatus === 'Unpaid' && (
                          <button
                            style={styles.payBtn}
                            onClick={() => navigate(`/payinvoice/${app.id}`, {
                              state: {
                                customer_id: app.customer_id,
                                vendor_id: app.vendor_id,
                                total: grandTotal,
                                start_time: app.start_time,
                                end_time: app.end_time,
                                date: formattedDate,
                                service_detail_id: app.product_detail_id || app.service_detail_id,
                              }
                            })}
                          >
                            Pay Invoice
                          </button>
                        )}
                        {(app.status === 'completed' || app.status === 'accepted') && payStatus === 'Paid' && (
                          <button
                            style={{ ...styles.payBtn, background: '#4078c0' }}
                            onClick={() => navigate(`/invoicedetails/${app.invoice_id}`, {
                              state: {
                                customer_id: app.customer_id,
                                vendor_id: app.vendor_id,
                                total: grandTotal,
                                start_time: app.start_time,
                                end_time: app.end_time,
                                date: formattedDate,
                                service_detail_id: app.product_detail_id || app.service_detail_id,
                              }
                            })}
                          >
                            Invoice Details
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      )}
      <button
        style={styles.backBtn}
        onClick={() => navigate('/customerdashboard')}
      >
        ← Return to Dashboard
      </button>
    </div>
  );
};

const styles = {
  container: {
    maxWidth: '1100px',
    margin: '40px auto',
    padding: '32px',
    background: '#fff',
    borderRadius: '18px',
    boxShadow: '0 6px 32px rgba(141,110,54,0.10)',
    fontFamily: 'Segoe UI, Arial, sans-serif',
  },
  title: {
    marginBottom: '38px',
    color: '#4078c0',
    fontSize: '2.2rem',
    textAlign: 'center',
    fontWeight: 'bold',
    letterSpacing: '1.5px',
  },
  tableWrapper: {
    overflowX: 'auto',
    borderRadius: '12px',
    boxShadow: '0 2px 12px #e0cfa0',
    background: '#f5f6fa',
    padding: '18px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    background: '#fff',
    borderRadius: '12px',
    overflow: 'hidden',
    fontSize: '1.05rem',
  },
  th: {
    background: '#f5f6fa',
    color: '#8d6e36',
    fontWeight: 'bold',
    fontSize: '1.08rem',
    padding: '14px 10px',
    borderBottom: '2px solid #e0cfa0',
    textAlign: 'left',
    letterSpacing: '0.5px',
  },
  thHourlyRate: {
    background: '#f5f6fa',
    color: '#8d6e36',
    fontWeight: 'bold',
    fontSize: '1.08rem',
    padding: '14px 22px', // wider
    borderBottom: '2px solid #e0cfa0',
    textAlign: 'left',
    letterSpacing: '0.5px',
    minWidth: '120px',
  },
  thPayStatus: {
    background: '#f5f6fa',
    color: '#8d6e36',
    fontWeight: 'bold',
    fontSize: '1.08rem',
    padding: '14px 22px', // wider
    borderBottom: '2px solid #e0cfa0',
    textAlign: 'left',
    letterSpacing: '0.5px',
    minWidth: '120px',
  },
  tr: {
    transition: 'background 0.2s',
  },
  td: {
    padding: '12px 10px',
    borderBottom: '1px solid #e0cfa0',
    fontSize: '1.05rem',
    textAlign: 'left',
    background: '#fff',
    verticalAlign: 'middle',
  },
  totalBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    background: '#f9fbe7',
    borderRadius: '8px',
    padding: '8px 12px',
    boxShadow: '0 1px 4px #e0cfa0',
    fontSize: '1rem',
    fontWeight: '500',
    color: '#333',
  },
  serviceFee: {
    color: '#8d6e36',
    marginBottom: '2px',
  },
  bookingFee: {
    color: '#388e3c',
    marginBottom: '2px',
  },
  grandTotal: {
    color: '#d32f2f',
    fontWeight: 'bold',
    marginTop: '2px',
  },
  payBtn: {
    background: '#388e3c',
    color: '#fff',
    fontWeight: 'bold',
    border: 'none',
    borderRadius: '8px',
    padding: '8px 22px',
    fontSize: '1rem',
    cursor: 'pointer',
    boxShadow: '0 1px 4px #c8e6c9',
    transition: 'background 0.2s',
    textDecoration: 'none',
    margin: '0 auto',
    display: 'block',
  },
  backBtn: {
    background: '#4078c0',
    color: '#fff',
    fontWeight: 'bold',
    border: 'none',
    borderRadius: '8px',
    padding: '12px 24px',
    fontSize: '1rem',
    cursor: 'pointer',
    boxShadow: '0 1px 4px #e0cfa0',
    transition: 'background 0.2s',
    textDecoration: 'none',
    marginTop: '32px',
    display: 'block',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  empty: {
    textAlign: 'center',
    color: '#888',
    fontSize: '1.18rem',
    marginTop: '32px',
    fontStyle: 'italic',
    fontWeight: '500',
  },
  loader: {
    textAlign: 'center',
    color: '#4078c0',
    fontSize: '1.2rem',
    fontWeight: 'bold',
    marginTop: '40px',
    letterSpacing: '1px',
  },
};

export default Invoices;